import { Box, Button, Link, Typography, useTheme } from '@mui/material';
import React from 'react';
import { Divider } from '@care/react-component-lib';
import { Icon24SuccessFilled } from '@care/react-embrace-icons';
import useResponsive from '@/components/hooks/useResponsive';
import { HIW_CAREGIVERS_PAGE_URL } from '@/constants';
import { AnalyticsEventName } from '@/types/enums';
import { AnalyticsHelper } from '@/utilities/AnalyticsHelper';

export const planItems = [
  {
    price: '$38.95',
    billingCycle: 'monthly',
    premiumBillingCycle: 'monthly',
    premiumDescriptions: [
      {
        summary: 'Lowest up front payment',
        description: `perfect for short-term solutions, cancel anytime`,
      },
    ],
  },
  {
    price: '$24.95',
    billingCycle: 'quarterly',
    premiumBillingCycle: 'quarterly',
    premiumDescriptions: [
      {
        summary: 'Save up to 36% vs monthly',
        description: `ongoing access with lower commitment than Annual`,
      },
      {
        summary: 'Ideal for planning ahead',
        description: `summer break, back to school and other seasonal needs`,
      },
    ],
  },
  {
    price: '$12.95',
    billingCycle: 'annually',
    premiumBillingCycle: 'annual',
    premiumDescriptions: [
      {
        summary: 'Save up to 65% vs monthly',
        description: `lowest price`,
      },
      {
        summary: 'Ideal for multiple needs',
        description: `when the whole household has a care schedule, annual is here`,
      },
      {
        summary: 'Stay covered',
        description: `because we know care is rarely a one-time arrangement`,
      },
    ],
  },
];

const FlexiblePlans = ({ getStartedHandler }: { getStartedHandler: () => void }) => {
  const { isDesktopLG } = useResponsive();
  const theme = useTheme();

  const getMarginSpacing = (index: number) => {
    if (index === planItems.length - 1) {
      return 2;
    }
    return isDesktopLG ? 3 : 0;
  };

  const learnMoreHandler = () => {
    AnalyticsHelper.logEvent({
      name: AnalyticsEventName.CTA_INTERACTED,
      data: {
        screen_name: 'Plans and pricing',
        enrollment_initiated: true,
        cta_text: 'Learn more',
        cta_location: 'Pricing',
        vertical: 'Unknown',
      },
    });
    window.location.assign(HIW_CAREGIVERS_PAGE_URL);
  };

  return (
    <Box
      id="plans"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        px: 3,
        mt: { xs: 8, lg: 10 },
      }}>
      <Typography
        variant="h1"
        sx={{
          textAlign: 'center',
          width: { xs: '230px', sm: '100%' },
          marginBottom: 3,
        }}>
        Flexible plans that fit your life
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { md: 2, lg: 4 },
        }}>
        {planItems.map(
          ({ price, billingCycle, premiumBillingCycle, premiumDescriptions }, index) => (
            <Box
              sx={{
                backgroundColor: theme.palette.background.default,
                minWidth: { xs: '328px', md: '280px' },
                minHeight: '257px',
                borderRadius: { xs: 6, md: 8 },
                justifyItems: 'flex-start',
                padding: 3,
                marginBottom: 4,
                maxWidth: {
                  sm: '373px',
                },
                ...(index === planItems.length - 1 && {
                  paddingTop: 0,
                  border: `2px solid ${theme.palette.brand.main}`,
                }),
              }}
              key={`plan-item-${billingCycle}`}>
              {premiumBillingCycle === 'annual' && (
                <Box
                  sx={{
                    color: theme.palette.text.brand,
                    backgroundColor: theme.palette.brand.accent,
                    padding: theme.spacing(0.5, 1, 0.5, 1),
                    borderRadius: '0 0 4px 4px',
                    justifySelf: 'flex-end',
                  }}>
                  <Typography variant="h3">Best value</Typography>
                </Box>
              )}
              <Typography
                sx={{
                  marginTop: getMarginSpacing(index),
                }}
                variant="h5">
                Starting from
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'baseline',
                  marginBottom: 0,
                }}>
                <Typography
                  sx={{
                    marginTop: 1,
                    marginBottom: 0.5,
                    fontSize: theme.typography.h1.fontSize,
                  }}
                  variant="h2">
                  {price}
                </Typography>
                <Typography variant="h4" sx={{ marginLeft: 1 }}>
                  / month
                </Typography>
                {isDesktopLG && (
                  <Typography sx={{ marginLeft: 1, fontWeight: 400 }} variant="h5">
                    (billed {billingCycle})
                  </Typography>
                )}
              </Box>
              {!isDesktopLG && (
                <Typography sx={{ fontWeight: 400 }} variant="h5">
                  (billed {billingCycle})
                </Typography>
              )}
              <Divider sx={{ marginY: 2 }} />
              <Typography variant="h2">Premium {premiumBillingCycle}</Typography>
              {premiumDescriptions.map(({ summary, description }, descriptionIndex) => (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    marginTop: 2,
                  }}
                  // eslint-disable-next-line react/no-array-index-key
                  key={`plan-item-${index}-description-${descriptionIndex}`}>
                  <Icon24SuccessFilled
                    size={24}
                    sx={{
                      '& path:first-of-type': {
                        fill: theme.palette.brand.main,
                      },
                    }}
                  />
                  <Box sx={{ marginLeft: 1.5 }}>
                    <Typography variant="body3">
                      <Box component="span" sx={{ fontWeight: 'bold' }}>
                        {summary}
                      </Box>
                      <Box component="span">&mdash;{description}</Box>
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )
        )}
      </Box>
      <Button
        onClick={getStartedHandler}
        size="large"
        sx={{ width: { xs: '100%', sm: '171px' }, height: '56px', borderRadius: '32px', mt: 4 }}
        color="primary"
        variant="contained">
        Get started
      </Button>
      <Box sx={{ marginTop: 3, marginBottom: { xs: 8, lg: 10 } }}>
        <Typography sx={{ fontWeight: 400 }} variant="h4">
          <Box component="span">Ready to earn as a caregiver? </Box>
          <Link
            onClick={learnMoreHandler}
            sx={{
              textDecoration: 'underline',
              color: theme.palette.text.link,
              '&:hover': {
                cursor: 'pointer',
              },
              fontWeight: theme.typography.fontWeightBold,
            }}
            component="span">
            Learn more
          </Link>
        </Typography>
      </Box>
    </Box>
  );
};

export default FlexiblePlans;
